/**
 * Report-header designer. Replicates the UX of the reference headers.html:
 *   5 fixed "element" rows (Logo + 4 text lines), each with its own inline
 *   property editor (size / weight / color / font / visibility) and a chip
 *   that shows a preview. Blue/grey re-skin.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderView, {
  DEFAULT_HEADER_CONFIG,
  HeaderConfig,
  HeaderGridRow,
  HeaderGridCell,
  CellSegment,
  StudyFields,
  TextElement,
  TextWeight,
  FONT_FAMILIES,
  cellToSegments,
  migrateHeaderConfig,
} from '../components/HeaderView';
import {
  loadSettingsFull, saveUserSettings, deleteUserSetting, SettingScope,
} from '../utils/userSettings';
import {
  cacheHeaderConfigLocally, clearCachedHeaderConfig,
} from '../utils/headerConfigCache';
import styles from './HeaderDesignerPage.module.css';

const SAMPLE_STUDY: StudyFields = {
  patientName: 'Doe^John', patientId: 'MRN-00421', patientDob: '1965-03-14',
  patientSex: 'M', patientAge: '60', accessionNumber: 'ACC-9876',
  studyDate: '2026-04-18', studyDescription: 'CT Chest with contrast',
  modality: 'CT', referringPhysician: 'Smith, A.', institution: 'Edge Imaging',
};

const AVAILABLE_FIELDS = [
  'patientName', 'patientId', 'patientDob', 'patientSex', 'patientAge',
  'accessionNumber', 'studyDate', 'studyDescription',
  'modality', 'referringPhysician', 'institution',
];

const TEXT_SIZES = Array.from({ length: 13 }, (_, i) => 8 + i);  // 8..20
const WEIGHT_OPTIONS: { value: TextWeight; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'bold',   label: 'Bold' },
  { value: 'black',  label: 'Black' },
];

type TextKey = 'hospitalName' | 'address' | 'department' | 'title';
const TEXT_ELEMENTS: { key: TextKey; label: string }[] = [
  { key: 'hospitalName', label: 'Hospital Name' },
  { key: 'address',      label: 'Hospital Address' },
  { key: 'department',   label: 'Department Name' },
  { key: 'title',        label: 'Report Title' },
];

export default function HeaderDesignerPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<HeaderConfig>(DEFAULT_HEADER_CONFIG);
  const [scope, setScope] = useState<SettingScope>('clinic');
  const [currentScope, setCurrentScope] = useState<SettingScope | undefined>();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [activeKey, setActiveKey] = useState<'logo' | TextKey | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function reload() {
    const s = await loadSettingsFull(true);
    if (s.values.reportHeader) {
      try { setConfig(migrateHeaderConfig(JSON.parse(s.values.reportHeader))); }
      catch { setConfig(DEFAULT_HEADER_CONFIG); }
    } else {
      setConfig(DEFAULT_HEADER_CONFIG);
    }
    setCurrentScope(s.scope.reportHeader);
  }

  useEffect(() => {
    document.title = 'Header Designer';
    void reload();
  }, []);

  // ---- logo helpers ----
  function patchLogo(patch: Partial<NonNullable<HeaderConfig['logo']>>) {
    setConfig({ ...config, logo: { ...(config.logo || {}), ...patch } });
  }
  /**
   * Downscale the picked image to a printable letterhead size and re-encode
   * before storing. We had a case where a user uploaded a full-resolution
   * scan of their letterhead - the base64 dataUri was 6+ MB, which shipped
   * as part of settings.reportHeader on every settings.get call. The
   * response.json() step in the browser silently failed on that payload,
   * so every report opened with the DEFAULT letterhead instead of the
   * user's design. This resize caps the logo at 800x300 px (well over what
   * any letterhead needs) and re-encodes as PNG, keeping the base64 blob
   * comfortably under 200 KB in practice.
   */
  const MAX_LOGO_WIDTH = 800;
  const MAX_LOGO_HEIGHT = 300;

  function onLogoPick(file: File | null) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const rawDataUri = reader.result as string;
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(1, MAX_LOGO_WIDTH / img.width, MAX_LOGO_HEIGHT / img.height);
        if (ratio >= 1) {
          // Image is already within limits - keep original bytes.
          patchLogo({ dataUri: rawDataUri });
          return;
        }
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          patchLogo({ dataUri: rawDataUri });
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        // PNG for lossless letterhead lines / logos with sharp edges.
        // If the resulting base64 is still huge (very unlikely at 800x300),
        // JPEG fallback would trade sharpness for size.
        patchLogo({ dataUri: canvas.toDataURL('image/png') });
      };
      img.onerror = () => patchLogo({ dataUri: rawDataUri }); // fallback: use raw
      img.src = rawDataUri;
    };
    reader.readAsDataURL(file);
  }

  // ---- text-element helpers ----
  function getText(key: TextKey): TextElement {
    return (config.elements?.[key] as TextElement) || {};
  }
  function patchText(key: TextKey, patch: Partial<TextElement>) {
    setConfig({
      ...config,
      elements: {
        ...(config.elements || {}),
        [key]: { ...getText(key), ...patch },
      },
    });
  }

  // ---- grid helpers ----

  /** Produce a HeaderGridCell in the new segment shape. Used when adding
   *  new cells; existing legacy cells stay in their `{fieldId, label}` form
   *  until the user edits their segments (at which point `cellToSegments`
   *  in the editor path returns segments and we save them). */
  function newCell(seed?: CellSegment[]): HeaderGridCell {
    return { colSpan: 1, segments: seed ?? [{ kind: 'text', text: 'Label: ' }, { kind: 'field', fieldId: 'patientName' }] };
  }

  function addRow() {
    setConfig({ ...config, grid: [...(config.grid || []), { cells: [newCell()] }] });
  }
  function removeRow(ri: number) {
    setConfig({ ...config, grid: (config.grid || []).filter((_, i) => i !== ri) });
  }
  function addCell(ri: number) {
    const grid = (config.grid || []).map((row, i) => (
      i === ri ? { cells: [...row.cells, newCell([{ kind: 'field', fieldId: 'studyDate' }])] } : row
    ));
    setConfig({ ...config, grid });
  }
  function removeCell(ri: number, ci: number) {
    const grid = (config.grid || []).map((row, i) => (
      i === ri ? { cells: row.cells.filter((_, j) => j !== ci) } : row
    ));
    setConfig({ ...config, grid });
  }

  /** Update a cell in-place. The updater receives the current cell with
   *  legacy `{fieldId, label}` already normalised into `segments`, but
   *  ALL other cell properties (colSpan, fontFamily, sizePt, weight,
   *  future additions) preserved via spread. Earlier version only kept
   *  colSpan + segments, which silently dropped every other property - a
   *  font-size change reset the font family, weight, and vice versa. */
  function mutateCell(ri: number, ci: number, updater: (cell: HeaderGridCell) => HeaderGridCell) {
    const grid = (config.grid || []).map((row, i) => {
      if (i !== ri) return row;
      return {
        cells: row.cells.map((cell, j) => {
          if (j !== ci) return cell;
          // Preserve everything on the cell; only replace fieldId/label
          // with the segments-derived form so downstream ops don't have
          // to check both shapes.
          const { fieldId: _lf, label: _ll, ...preserved } = cell;
          const normalised: HeaderGridCell = {
            ...preserved,
            colSpan: cell.colSpan || 1,
            segments: cellToSegments(cell),
          };
          return updater(normalised);
        }),
      };
    });
    setConfig({ ...config, grid });
  }

  function updateCellColSpan(ri: number, ci: number, colSpan: number) {
    mutateCell(ri, ci, (cell) => ({ ...cell, colSpan }));
  }
  function updateCellFont(ri: number, ci: number, patch: Partial<Pick<HeaderGridCell, 'fontFamily' | 'sizePt' | 'weight' | 'color'>>) {
    mutateCell(ri, ci, (cell) => ({ ...cell, ...patch }));
  }

  /** Copy the source cell's font family / size / weight / color to EVERY
   *  other cell in the whole grid (across every row). Preserves each
   *  cell's own segments, colSpan, and legacy fields - only the font
   *  properties are overwritten. Useful once the designer has a look
   *  they like and want it applied uniformly. */
  function cloneCellStyleToAll(sourceRi: number, sourceCi: number) {
    const grid = config.grid || [];
    const src = grid[sourceRi]?.cells[sourceCi];
    if (!src) return;
    const stylePatch = {
      fontFamily: src.fontFamily,
      sizePt: src.sizePt,
      weight: src.weight,
      color: src.color,
    };
    const cellsChanged = grid.reduce((n, r) => n + r.cells.length, 0) - 1;
    if (cellsChanged <= 0) return;
    if (!confirm(`Apply this cell's font settings to all ${cellsChanged} other cell${cellsChanged === 1 ? '' : 's'}?`)) return;
    const newGrid = grid.map((row, ri) => ({
      cells: row.cells.map((cell, ci) => {
        if (ri === sourceRi && ci === sourceCi) return cell;
        return { ...cell, ...stylePatch };
      }),
    }));
    setConfig({ ...config, grid: newGrid });
  }
  function updateSegment(ri: number, ci: number, si: number, patch: Partial<CellSegment>) {
    mutateCell(ri, ci, (cell) => ({
      ...cell,
      segments: (cell.segments || []).map((seg, k) => (
        k === si ? ({ ...seg, ...patch } as CellSegment) : seg
      )),
    }));
  }
  function addSegment(ri: number, ci: number, seg: CellSegment) {
    mutateCell(ri, ci, (cell) => ({ ...cell, segments: [...(cell.segments || []), seg] }));
  }
  function removeSegment(ri: number, ci: number, si: number) {
    mutateCell(ri, ci, (cell) => ({
      ...cell,
      segments: (cell.segments || []).filter((_, k) => k !== si),
    }));
  }

  // ---- save / revert / reset ----
  async function save() {
    setSaving(true);
    setMessage('');
    const json = JSON.stringify(config);
    // Always write the local backup FIRST so even if the server save fails,
    // reports will still show the user's design via the fallback path.
    cacheHeaderConfigLocally(json);
    const ok = await saveUserSettings({ reportHeader: json }, scope);
    setSaving(false);
    if (ok) {
      await reload();
      setMessage(scope === 'clinic' ? 'Saved as clinic default' : 'Saved as personal override');
    } else {
      setMessage('Save failed (server) - kept locally as fallback');
    }
    setTimeout(() => setMessage(''), 2500);
  }
  async function revertPersonal() {
    if (!confirm('Remove your personal header override and use the clinic default?')) return;
    const ok = await deleteUserSetting('reportHeader', 'user');
    if (ok) {
      clearCachedHeaderConfig();
      await reload();
      setMessage('Reverted to clinic default');
      setTimeout(() => setMessage(''), 2000);
    }
  }
  function resetToDefault() {
    if (!confirm('Reset header to default layout? Unsaved changes will be lost.')) return;
    setConfig(DEFAULT_HEADER_CONFIG);
  }

  // ---- renderers ----
  const renderLogoChip = () => {
    const logo = config.logo || {};
    const visible = logo.visible !== false;
    const active = activeKey === 'logo';
    return (
      <div className={`${styles.elementItem} ${active ? styles.elementActive : ''}`}>
        <div className={styles.elementHeader}
          onClick={() => setActiveKey(active ? null : 'logo')}
        >
          <span className={styles.chipSwatch} style={{ background: '#e2e8f0' }}>
            {logo.dataUri ? <img src={logo.dataUri} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : '—'}
          </span>
          <div className={styles.chipLabel}>
            <div className={styles.chipTitle}>Hospital Logo</div>
            <div className={styles.chipPreview}>
              {logo.dataUri ? `${logo.maxWidth || 150}×${logo.maxHeight || 50}px, ${logo.position || 'left'}` : 'No logo'}
            </div>
          </div>
          <button
            className={styles.eye}
            title={visible ? 'Hide' : 'Show'}
            onClick={(e) => { e.stopPropagation(); patchLogo({ visible: !visible }); }}
          >
            {visible ? '👁' : '🚫'}
          </button>
        </div>
        {active && (
          <div className={styles.propEditor}>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => onLogoPick(e.target.files?.[0] || null)}
            />
            <div className={styles.peRow}>
              <button className={styles.smallBtn} onClick={() => fileInput.current?.click()}>
                📁 Upload
              </button>
              <button className={styles.smallDangerBtn} onClick={() => patchLogo({ dataUri: null })}>
                🗑 Clear
              </button>
            </div>
            <div className={styles.pe3col}>
              <label className={styles.peLabel}>
                Max Width (px)
                <input
                  type="number"
                  className={styles.input}
                  value={config.logo?.maxWidth ?? 150}
                  onChange={(e) => patchLogo({ maxWidth: Math.max(16, Number(e.target.value)) })}
                />
              </label>
              <label className={styles.peLabel}>
                Max Height (px)
                <input
                  type="number"
                  className={styles.input}
                  value={config.logo?.maxHeight ?? 50}
                  onChange={(e) => patchLogo({ maxHeight: Math.max(16, Number(e.target.value)) })}
                />
              </label>
              <label className={styles.peLabel}>
                Position
                <select
                  className={styles.input}
                  value={config.logo?.position || 'left'}
                  onChange={(e) => patchLogo({ position: e.target.value as any })}
                >
                  <option value="above">Above Text</option>
                  <option value="left">Left of Text</option>
                  <option value="right">Right of Text</option>
                </select>
              </label>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTextChip = (k: TextKey, label: string) => {
    const el = getText(k);
    const visible = el.visible !== false;
    const active = activeKey === k;
    return (
      <div key={k} className={`${styles.elementItem} ${active ? styles.elementActive : ''}`}>
        <div className={styles.elementHeader}
          onClick={() => setActiveKey(active ? null : k)}
        >
          <span className={styles.chipSwatch} style={{ background: el.color || '#718096' }} />
          <div className={styles.chipLabel}>
            <div className={styles.chipTitle}>{label}</div>
            <div className={styles.chipPreview} style={{ opacity: visible ? 1 : 0.45 }}>
              {el.text || '(empty)'}
            </div>
          </div>
          <button
            className={styles.eye}
            title={visible ? 'Hide' : 'Show'}
            onClick={(e) => { e.stopPropagation(); patchText(k, { visible: !visible }); }}
          >
            {visible ? '👁' : '🚫'}
          </button>
        </div>
        {active && (
          <div className={styles.propEditor}>
            <input
              className={styles.input}
              value={el.text || ''}
              onChange={(e) => patchText(k, { text: e.target.value })}
              placeholder="Text content"
            />
            <div className={styles.pe3col}>
              <label className={styles.peLabel}>
                Size
                <select
                  className={styles.input}
                  value={el.sizePt ?? 12}
                  onChange={(e) => patchText(k, { sizePt: Number(e.target.value) })}
                >
                  {TEXT_SIZES.map((s) => <option key={s} value={s}>{s}pt</option>)}
                </select>
              </label>
              <label className={styles.peLabel}>
                Weight
                <select
                  className={styles.input}
                  value={el.weight || 'normal'}
                  onChange={(e) => patchText(k, { weight: e.target.value as TextWeight })}
                >
                  {WEIGHT_OPTIONS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                </select>
              </label>
              <label className={styles.peLabel}>
                Color
                <input
                  type="color"
                  className={styles.input}
                  style={{ padding: 0, height: 28 }}
                  value={el.color || '#000000'}
                  onChange={(e) => patchText(k, { color: e.target.value })}
                />
              </label>
            </div>
            <label className={styles.peLabel}>
              Font
              <select
                className={styles.input}
                value={el.fontFamily || FONT_FAMILIES[0].value}
                onChange={(e) => patchText(k, { fontFamily: e.target.value })}
              >
                {FONT_FAMILIES.map((f) => (
                  <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <button className={styles.backBtn} onClick={() => navigate('/worklist')}>
          ← Back
        </button>
        <h2 className={styles.title}>Report Header Designer</h2>
        {currentScope && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
            background: currentScope === 'user' ? '#d69e2e' : '#1a4971',
            color: 'white', textTransform: 'uppercase', letterSpacing: 0.5,
          }}>
            Currently: {currentScope === 'user' ? 'Personal override' : 'Clinic default'}
          </span>
        )}
        <div style={{ flex: 1 }} />

        <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'white' }}>
          <label style={{ cursor: 'pointer' }}>
            <input type="radio" checked={scope === 'clinic'} onChange={() => setScope('clinic')} />{' '}
            Clinic default
          </label>
          <label style={{ cursor: 'pointer' }}>
            <input type="radio" checked={scope === 'user'} onChange={() => setScope('user')} />{' '}
            Just me
          </label>
        </div>

        {message && <span className={styles.message}>{message}</span>}
        {currentScope === 'user' && (
          <button className={styles.secondaryBtn} onClick={revertPersonal} title="Delete personal override">
            Revert personal
          </button>
        )}
        <button className={styles.secondaryBtn} onClick={resetToDefault}>Reset form</button>
        <button className={styles.primaryBtn} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : (scope === 'clinic' ? 'Save for Clinic' : 'Save for Me')}
        </button>
      </div>

      <div className={styles.body}>
        {/* Designer panel */}
        <div className={styles.designer}>
          <h3 className={styles.sectionTitle}>Header Elements</h3>
          <div style={{ fontSize: 11, color: '#718096', marginBottom: 8 }}>
            Click an element to edit. Use the eye to hide/show.
          </div>
          {renderLogoChip()}
          {TEXT_ELEMENTS.map((t) => renderTextChip(t.key, t.label))}

          <h3 className={styles.sectionTitle}>Study Info Grid</h3>
          <div style={{ fontSize: 11, color: '#718096', marginBottom: 8 }}>
            Each row is a horizontal band. Cells lay out left→right with their colSpan.
          </div>
          {(config.grid || []).map((row, ri) => (
            <div key={ri} className={styles.rowBlock}>
              <div className={styles.rowHead}>
                <strong>Row {ri + 1}</strong>
                <div style={{ flex: 1 }} />
                <button className={styles.smallBtn} onClick={() => addCell(ri)}>+ Cell</button>
                <button className={styles.smallDangerBtn} onClick={() => removeRow(ri)}>Delete Row</button>
              </div>
              {row.cells.map((cell, ci) => {
                const segments = cellToSegments(cell);
                return (
                  <div key={ci} style={{
                    border: '1px solid #cbd5e0', borderRadius: 4,
                    padding: 8, marginBottom: 6, background: '#f7fafc',
                  }}>
                    {/* Line 1: Cell N label + span input + remove button */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <strong style={{ fontSize: 12, color: '#4a5568' }}>Cell {ci + 1}</strong>
                      <div style={{ flex: 1 }} />
                      <label style={{ fontSize: 11, color: '#4a5568', display: 'flex', alignItems: 'center', gap: 4 }}>
                        span
                        <input
                          type="number"
                          min={1}
                          max={4}
                          className={styles.input}
                          style={{ width: 50 }}
                          value={cell.colSpan || 1}
                          onChange={(e) => updateCellColSpan(ri, ci, Math.max(1, Number(e.target.value)))}
                        />
                      </label>
                      <button
                        className={styles.smallDangerBtn}
                        title="Remove this cell"
                        onClick={() => removeCell(ri, ci)}
                      >× Cell</button>
                    </div>

                    {/* Line 2: font family / size / weight / color / clone */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <select
                        className={styles.input}
                        style={{ flex: 1, fontSize: 11 }}
                        title="Font family for this cell"
                        value={cell.fontFamily || ''}
                        onChange={(e) => updateCellFont(ri, ci, { fontFamily: e.target.value || undefined })}
                      >
                        <option value="">Default font</option>
                        {FONT_FAMILIES.map((f) => (
                          <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                      <select
                        className={styles.input}
                        style={{ width: 80, fontSize: 11 }}
                        title="Font size for this cell"
                        value={cell.sizePt ?? ''}
                        onChange={(e) => updateCellFont(ri, ci, { sizePt: e.target.value ? Number(e.target.value) : undefined })}
                      >
                        <option value="">Size</option>
                        {TEXT_SIZES.map((s) => <option key={s} value={s}>{s}pt</option>)}
                      </select>
                      <select
                        className={styles.input}
                        style={{ width: 90, fontSize: 11 }}
                        title="Weight for this cell"
                        value={cell.weight || 'normal'}
                        onChange={(e) => updateCellFont(ri, ci, { weight: e.target.value as TextWeight })}
                      >
                        {WEIGHT_OPTIONS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                      </select>
                      <input
                        type="color"
                        className={styles.input}
                        title="Text colour for this cell"
                        style={{ width: 40, padding: 0, height: 28 }}
                        value={cell.color || '#000000'}
                        onChange={(e) => updateCellFont(ri, ci, { color: e.target.value })}
                      />
                      <button
                        className={styles.smallBtn}
                        title="Apply this cell's font family / size / weight / colour to every other cell in the grid"
                        onClick={() => cloneCellStyleToAll(ri, ci)}
                      >Clone to all</button>
                    </div>

                    {/* Segment list */}
                    {segments.length === 0 && (
                      <div style={{ fontSize: 11, color: '#718096', fontStyle: 'italic', marginBottom: 6 }}>
                        Empty cell. Add text or a placeholder below.
                      </div>
                    )}
                    {segments.map((seg, si) => (
                      <div key={si} className={styles.cellRow} style={{ alignItems: 'center' }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
                          background: seg.kind === 'field' ? '#bee3f8' : '#e2e8f0',
                          color: seg.kind === 'field' ? '#2b6cb0' : '#4a5568',
                          minWidth: 60, textAlign: 'center',
                        }}>
                          {seg.kind === 'field' ? 'FIELD' : 'TEXT'}
                        </span>
                        {seg.kind === 'text' ? (
                          <input
                            className={styles.input}
                            style={{ flex: 1 }}
                            placeholder='e.g. "Patient: " or " / "'
                            value={seg.text}
                            onChange={(e) => updateSegment(ri, ci, si, { text: e.target.value })}
                          />
                        ) : (
                          <select
                            className={styles.input}
                            style={{ flex: 1 }}
                            value={seg.fieldId}
                            onChange={(e) => updateSegment(ri, ci, si, { fieldId: e.target.value })}
                          >
                            {AVAILABLE_FIELDS.map((f) => (
                              <option key={f} value={f}>{f}</option>
                            ))}
                          </select>
                        )}
                        <button
                          className={styles.smallDangerBtn}
                          title="Remove segment"
                          onClick={() => removeSegment(ri, ci, si)}
                        >×</button>
                      </div>
                    ))}

                    {/* Add-segment buttons */}
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <button
                        className={styles.smallBtn}
                        onClick={() => addSegment(ri, ci, { kind: 'text', text: '' })}
                      >+ Text</button>
                      <button
                        className={styles.smallBtn}
                        onClick={() => addSegment(ri, ci, { kind: 'field', fieldId: 'patientName' })}
                      >+ Placeholder</button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          <button className={styles.secondaryBtn} style={{ color: '#2b6cb0', background: '#edf2f7', border: '1px solid #cbd5e0' }} onClick={addRow}>
            + Add Row
          </button>
        </div>

        {/* Live preview */}
        <div className={styles.preview}>
          <div className={styles.previewLabel}>Preview (sample study data)</div>
          <div className={styles.previewFrame}>
            <HeaderView config={config} study={SAMPLE_STUDY} />
            <div style={{ padding: 20, color: '#718096', fontStyle: 'italic', fontSize: 12 }}>
              (Report body / findings appear below — edited in the report editor.)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
